import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Plus, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ProductCard = ({ product, categoryName, featured = false }) => {
  const { addItem } = useCart();
  const isComingSoon = product.status === 'coming_soon';

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isComingSoon) {
      toast.info('This product is coming soon!');
      return;
    }
    addItem(product);
    toast.success(`${product.title} added to cart`);
  };

  // Format category display - use name if provided, hide if it's a UUID
  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
  const displayCategory = categoryName || (isUUID(product.category_id) ? null : product.category_id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className={`group relative bg-neutral-900/40 backdrop-blur-sm border border-white/5 overflow-hidden hover:border-cyan-400/50 transition-all duration-500 flex flex-col ${
        featured ? 'row-span-2' : ''
      }`}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-void-subtle">
        <img
          src={product.images[0]?.url}
          alt={product.title}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${isComingSoon ? 'opacity-90' : ''}`}
        />
        
        {/* Coming Soon Banner */}
        {isComingSoon && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
            <div className="relative z-10 bg-amber-500 text-black px-6 py-2 transform -rotate-12 shadow-lg">
              <span className="font-mono text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Coming Soon!
              </span>
            </div>
          </div>
        )}
        
        {/* Quick Add Button - hide for coming soon */}
        {!isComingSoon && (
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={handleAddToCart}
            className="absolute bottom-4 right-4 w-12 h-12 bg-cyan-400 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-cyan-400-hover"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col">
        {displayCategory && (
          <span className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">
            {displayCategory}
          </span>
        )}
        <h3 className="font-heading text-lg font-semibold text-white mb-4 group-hover:text-cyan-400 transition-colors">
          {product.title}
        </h3>
        <div className="flex items-center justify-between mt-auto">
          <span className="font-mono text-lg text-cyan-400">
            ${product.price.toFixed(2)}
          </span>
          {isComingSoon ? (
            <span className="text-xs font-mono uppercase tracking-widest text-amber-500">
              Coming Soon
            </span>
          ) : (
            <span className="text-xs font-mono uppercase tracking-widest text-neutral-600">
              {product.dimensions?.split('(')[0] || ''}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
